// assets/flocage-fields.js
// Standalone custom element (no theme imports) that reveals the flocage
// name/number fields only when a real "Flocage" model variant is selected.
// NOTE: the default option "Sans Flocage" also contains the word "flocage",
// so we require the keyword AND the absence of an exclude word ("sans").
// Typed values persist across the theme's section re-render on variant change.

const STORE = new Map(); // name -> value, survives section morph/re-create

class FlocageFields extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || document;
    this._onChange = () => this.update();
    // Theme dispatches `variant:update` on document after a variant change.
    document.addEventListener('variant:update', this._onChange);
    document.addEventListener('variant:selected', this._onChange);
    // Also catch direct interaction with the variant picker.
    this.section.addEventListener('change', this._onChange);
    this.addEventListener('input', this._save.bind(this));
    // Re-evaluate shortly after load in case the picker hydrates late.
    this._restore();
    this.update();
    setTimeout(() => this.update(), 300);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this._onChange);
    document.removeEventListener('variant:selected', this._onChange);
    this.section?.removeEventListener('change', this._onChange);
  }

  get keyword() {
    return (this.getAttribute('data-keyword') || 'flocage').toLowerCase();
  }
  get exclude() {
    return (this.getAttribute('data-exclude') || 'sans').toLowerCase();
  }
  get fields() {
    return Array.from(this.querySelectorAll('input, textarea'));
  }

  // Collect the text of every *selected* option (radio value + its label text,
  // and any <select> value/label) within this product section.
  _selectedValues() {
    const scope = this.section || document;
    const out = [];
    scope.querySelectorAll('input:checked').forEach((i) => {
      out.push(i.value || '');
      if (i.id) {
        try {
          const l = scope.querySelector(`label[for="${(window.CSS && CSS.escape) ? CSS.escape(i.id) : i.id}"]`);
          if (l) out.push(l.textContent || '');
        } catch (e) { /* ignore bad selector */ }
      }
      const wrap = i.closest('label');
      if (wrap) out.push(wrap.textContent || '');
    });
    scope.querySelectorAll('select').forEach((s) => {
      out.push(s.value || '');
      const opt = s.options[s.selectedIndex];
      if (opt) out.push(opt.textContent || '');
    });
    return out.map((v) => (v || '').toLowerCase().trim()).filter(Boolean);
  }

  _save() {
    for (const el of this.fields) if (el.name) STORE.set(el.name, el.value);
  }
  _restore() {
    for (const el of this.fields) if (el.name && STORE.has(el.name) && !el.value) el.value = STORE.get(el.name);
  }

  update() {
    const kw = this.keyword;
    const ex = this.exclude;
    const show = this._selectedValues().some((v) => v.includes(kw) && !v.includes(ex));
    this.hidden = !show;
    for (const el of this.fields) {
      // Disabled fields aren't submitted, so hidden flocage props never leak in.
      el.disabled = !show;
      el.required = show && el.dataset.required === 'true';
    }
    if (show) this._restore();
  }
}

if (!customElements.get('flocage-fields')) {
  customElements.define('flocage-fields', FlocageFields);
}
